import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';
import PTOBalanceCard from '../components/PTOBalanceCard';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle, selectStyle } from '../../styles/tokens';

const TYPE_OPTIONS = [
  { value: 'vacation', label: 'Vacaciones' },
  { value: 'sick',     label: 'Enfermedad' },
  { value: 'personal', label: 'Personal' },
];

function countBusinessDays(startStr, endStr, holidays = []) {
  if (!startStr || !endStr) return 0;
  const start = new Date(startStr + 'T00:00:00');
  const end   = new Date(endStr   + 'T00:00:00');
  // Normalize holiday dates to YYYY-MM-DD for reliable comparison
  const holidaySet = new Set(holidays.map(h => {
    const d = h.date || h;
    return typeof d === 'string' ? d.slice(0, 10) : new Date(d).toISOString().slice(0, 10);
  }));
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const day = cur.getDay();
    const dateStr = cur.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !holidaySet.has(dateStr)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

export default function RequestCreatePage() {
  const navigate = useNavigate();
  const [balances, setBalances] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [type, setType] = useState('vacation');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    client.get('/api/vacation/balances').then(r => setBalances(r.data)).catch(console.error);
    client.get('/api/vacation/supervisors').then(r => setSupervisors(r.data)).catch(console.error);
    client.get('/api/admin/holidays').then(r => setHolidays((r.data || []).filter(h => h.is_active !== false))).catch(() => setHolidays([]));
  }, []);

  const daysRequested = useMemo(() => countBusinessDays(startDate, endDate, holidays), [startDate, endDate, holidays]);

  // Find holidays within selected range to show user
  const holidaysInRange = useMemo(() => {
    if (!startDate || !endDate || !holidays.length) return [];
    return holidays.filter(h => {
      const d = (h.date || '').slice(0, 10);
      return d >= startDate && d <= endDate;
    });
  }, [startDate, endDate, holidays]);

  const selectedBalance = balances.find(b => b.type === type);
  const available = selectedBalance
    ? parseFloat(selectedBalance.total_days) - parseFloat(selectedBalance.used_days) - parseFloat(selectedBalance.pending_days)
    : null;

  const todayStr = new Date().toISOString().slice(0, 10);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!supervisorId) {
      setError('Debe seleccionar un supervisor (Nivel 1).');
      return;
    }
    if (!startDate || !endDate) {
      setError('Selecciona las fechas de inicio y fin.');
      return;
    }
    if (daysRequested <= 0) {
      setError('El rango seleccionado no contiene días hábiles.');
      return;
    }
    if (available !== null && daysRequested > available) {
      setError(`Saldo insuficiente. Disponible: ${available} días, solicitado: ${daysRequested} días.`);
      return;
    }

    setSubmitting(true);
    try {
      await client.post('/api/vacation/requests', {
        type, start_date: startDate, end_date: endDate,
        reason: reason || null, supervisor_id: parseInt(supervisorId),
      });
      navigate('/vacaciones');
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'pto_request_create',
          pto_type: type,
          days_requested: daysRequested,
        },
      });
      setError(err.response?.data?.error || 'Error al crear solicitud.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 600, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/vacaciones')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, sans-serif' }}
      >
        <ArrowLeft size={16} /> Volver a solicitudes
      </button>

      <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Nueva Solicitud</h1>

      <PTOBalanceCard balances={balances} />

      <form onSubmit={handleSubmit} style={{ ...bubbleStyle, padding: '20px 24px' }}>
        {/* Supervisor (L1) picker */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Supervisor (Nivel 1)
          </label>
          <select value={supervisorId} onChange={e => setSupervisorId(e.target.value)} style={selectStyle} required>
            <option value="">— Seleccione supervisor —</option>
            {supervisors.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Este supervisor será el primer aprobador de su solicitud.
          </div>
        </div>

        {/* Type */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Tipo de ausencia
          </label>
          <select value={type} onChange={e => setType(e.target.value)} style={selectStyle}>
            {TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              Fecha de inicio
            </label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); if (endDate && e.target.value > endDate) setEndDate(e.target.value); }}
              min={todayStr}
              style={fieldStyle}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
              Fecha de fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              min={startDate || todayStr}
              style={fieldStyle}
              required
            />
          </div>
        </div>

        {/* Days summary with balance math */}
        {daysRequested > 0 && (
          <div style={{
            background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8,
            padding: '10px 14px', marginBottom: 16, fontSize: 13,
          }}>
            <strong style={{ color: '#0369a1' }}>{daysRequested} día{daysRequested !== 1 ? 's' : ''} hábil{daysRequested !== 1 ? 'es' : ''}</strong>
            {available !== null && (
              <>
                <span style={{ color: '#64748b' }}>
                  {' '}· Saldo disponible: <strong style={{ color: daysRequested > available ? '#dc2626' : '#16a34a' }}>{available}</strong>
                </span>
                <span style={{ color: '#64748b' }}>
                  {' '}· Saldo después de solicitud: <strong style={{ color: (available - daysRequested) < 0 ? '#dc2626' : '#16a34a' }}>
                    {(available - daysRequested).toFixed(1)}
                  </strong>
                </span>
              </>
            )}
            {holidaysInRange.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#0369a1' }}>
                Feriados excluidos ({holidaysInRange.length}):
                {holidaysInRange.map((h, i) => (
                  <span key={i} style={{ marginLeft: 4 }}>
                    {h.name} ({new Date((typeof h.date === 'string' ? h.date.slice(0, 10) : h.date) + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })})
                    {i < holidaysInRange.length - 1 ? ',' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reason */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            Motivo <span style={{ fontWeight: 400, color: '#94a3b8' }}>(opcional)</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Ej: Viaje familiar"
            style={{ ...fieldStyle, resize: 'vertical' }}
          />
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca',
            borderRadius: 6, padding: '8px 12px', fontSize: 13, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            ...navyBtn, width: '100%',
            opacity: submitting ? 0.6 : 1,
            cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Enviando...' : 'Enviar Solicitud'}
        </button>
      </form>
    </div>
  );
}
